import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import MobileFrame from "./components/MobileFrame";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import CustomerHome from "./pages/CustomerHome";
import ServiceCategory from "./pages/ServiceCategory";
import ServiceDetail from "./pages/ServiceDetail";
import Subscriptions from "./pages/Subscriptions";
import SubscriptionDetail from "./pages/SubscriptionDetail";
import StaffLogin from "./pages/StaffLogin";
import StaffDashboard from "./pages/StaffDashboard";
import { Terms, Privacy, Refunds, Support, About } from "./pages/LegalPages";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import BookingConfirm from "./pages/BookingConfirm";
import Bookings from "./pages/Bookings";
import Profile from "./pages/Profile";
import Wallet from "./pages/Wallet";
import Rewards from "./pages/Rewards";
import Referral from "./pages/Referral";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import CleanerDashboard from "./pages/CleanerDashboard";
import CleanerJobs from "./pages/CleanerJobs";
import CleanerEarnings from "./pages/CleanerEarnings";
import CleanerAttendance from "./pages/CleanerAttendance";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminServices from "./pages/admin/AdminServices";
import AdminOffers from "./pages/admin/AdminOffers";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminWorkers from "./pages/admin/AdminWorkers";
import AdminWallet from "./pages/admin/AdminWallet";
import AdminRewards from "./pages/admin/AdminRewards";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminFranchises from "./pages/admin/AdminFranchises";
import AdminErp from "./pages/admin/AdminErp";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminAccounts from "./pages/admin/AdminAccounts";
import AdminReports from "./pages/admin/AdminReports";
import AdminTax from "./pages/admin/AdminTax";
import AdminPermissions from "./pages/admin/AdminPermissions";
import AdminImportUsers from "./pages/admin/AdminImportUsers";
import FranchisePortal from "./pages/franchise/FranchisePortal";
import FranchiseLogin from "./pages/franchise/FranchiseLogin";
import HOPortal from "./pages/ho/HOPortal";
import HOLogin from "./pages/ho/HOLogin";

const RequireAuth = ({ children }) => {
  const { user } = useApp();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const RequireAdmin = ({ children }) => {
  const admin = localStorage.getItem('c24_admin');
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

const RequireStaff = ({ children }) => {
  const staff = localStorage.getItem('c24_staff');
  if (!staff) return <Navigate to="/staff/login" replace />;
  return children;
};

// Handles Emergent Google Auth callback: reads #session_id from URL fragment
const GoogleAuthCallback = () => {
  const { googleLogin } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      const sid = decodeURIComponent(m[1]);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      googleLogin(sid).then((u) => {
        navigate(u.role === 'cleaner' ? '/cleaner/dashboard' : '/home', { replace: true });
      }).catch((e) => console.warn('Google auth exchange failed', e?.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <MobileFrame>
          <GoogleAuthCallback />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/cleaner/login" element={<Login cleanerMode />} />
            <Route path="/cleaner/signup" element={<Signup cleanerMode />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/support" element={<Support />} />
            <Route path="/about" element={<About />} />

            {/* Customer */}
            <Route path="/home" element={<RequireAuth><CustomerHome /></RequireAuth>} />
            <Route path="/services/:categoryId" element={<RequireAuth><ServiceCategory /></RequireAuth>} />
            <Route path="/services/:categoryId/:serviceId" element={<RequireAuth><ServiceDetail /></RequireAuth>} />
            <Route path="/cart" element={<RequireAuth><Cart /></RequireAuth>} />
            <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
            <Route path="/booking/:bookingId" element={<RequireAuth><BookingConfirm /></RequireAuth>} />
            <Route path="/bookings" element={<RequireAuth><Bookings /></RequireAuth>} />
            <Route path="/subscriptions" element={<RequireAuth><Subscriptions /></RequireAuth>} />
            <Route path="/subscription/:planId" element={<RequireAuth><SubscriptionDetail /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
            <Route path="/wallet" element={<RequireAuth><Wallet /></RequireAuth>} />
            <Route path="/rewards" element={<RequireAuth><Rewards /></RequireAuth>} />
            <Route path="/referral" element={<RequireAuth><Referral /></RequireAuth>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            {/* Cleaner */}
            <Route path="/cleaner/dashboard" element={<RequireAuth><CleanerDashboard /></RequireAuth>} />
            <Route path="/cleaner/jobs" element={<RequireAuth><CleanerJobs /></RequireAuth>} />
            <Route path="/cleaner/earnings" element={<RequireAuth><CleanerEarnings /></RequireAuth>} />
            <Route path="/cleaner/attendance" element={<RequireAuth><CleanerAttendance /></RequireAuth>} />
            <Route path="/cleaner/profile" element={<RequireAuth><Profile /></RequireAuth>} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            <Route path="/admin/categories" element={<RequireAdmin><AdminCategories /></RequireAdmin>} />
            <Route path="/admin/services" element={<RequireAdmin><AdminServices /></RequireAdmin>} />
            <Route path="/admin/offers" element={<RequireAdmin><AdminOffers /></RequireAdmin>} />
            <Route path="/admin/bookings" element={<RequireAdmin><AdminBookings /></RequireAdmin>} />
            <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
            <Route path="/admin/workers" element={<RequireAdmin><AdminWorkers /></RequireAdmin>} />
            <Route path="/admin/wallet" element={<RequireAdmin><AdminWallet /></RequireAdmin>} />
            <Route path="/admin/rewards" element={<RequireAdmin><AdminRewards /></RequireAdmin>} />
            <Route path="/admin/referrals" element={<RequireAdmin><AdminReferrals /></RequireAdmin>} />
            <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
            <Route path="/admin/franchises" element={<RequireAdmin><AdminFranchises /></RequireAdmin>} />
            <Route path="/admin/erp" element={<RequireAdmin><AdminErp /></RequireAdmin>} />
            <Route path="/admin/approvals" element={<RequireAdmin><AdminApprovals /></RequireAdmin>} />
            <Route path="/admin/accounts" element={<RequireAdmin><AdminAccounts /></RequireAdmin>} />
            <Route path="/admin/reports" element={<RequireAdmin><AdminReports /></RequireAdmin>} />
            <Route path="/admin/tax" element={<RequireAdmin><AdminTax /></RequireAdmin>} />
            <Route path="/admin/permissions" element={<RequireAdmin><AdminPermissions /></RequireAdmin>} />
            <Route path="/admin/import-users" element={<RequireAdmin><AdminImportUsers /></RequireAdmin>} />
            <Route path="/franchise/login" element={<FranchiseLogin />} />
            <Route path="/franchise" element={<RequireAuth><FranchisePortal /></RequireAuth>} />
            <Route path="/ho/login" element={<HOLogin />} />
            <Route path="/ho" element={<RequireAuth><HOPortal /></RequireAuth>} />

            {/* Staff / Supervisor */}
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/staff" element={<RequireStaff><StaffDashboard /></RequireStaff>} />
          </Routes>
        </MobileFrame>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
